import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, Divider, Spin, Image } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSiteConfig } from '../../contexts/SiteConfigContext';

const { Title, Text } = Typography;

const Register: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { siteConfig, loading: configLoading, refreshConfig } = useSiteConfig();

  // 注释：不再强制刷新配置，使用缓存的配置
  // useEffect(() => {
  //   console.log('🔄 注册页面：强制刷新配置');
  //   refreshConfig();
  // }, []);

  const onFinish = async (values: { 
    username: string; 
    email: string; 
    password: string; 
    confirmPassword: string; 
  }) => {
    if (values.password !== values.confirmPassword) {
      return;
    }

    setLoading(true);
    try {
      const success = await register(values.username, values.email, values.password);
      if (success) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <Card className="w-full max-w-md shadow-2xl">
        <div className="text-center mb-8">
          {configLoading ? (
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Spin size="large" />
            </div>
          ) : siteConfig?.logo_url ? (
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Image
                src={siteConfig.logo_url}
                alt="网站LOGO"
                width={64}
                height={64}
                className="rounded-full object-cover"
                placeholder={
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-2xl">G</span>
                  </div>
                }
                onError={() => {
                  // 图片加载失败时的处理
                  console.log('LOGO图片加载失败');
                }}
              />
            </div>
          ) : (
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">G</span>
            </div>
          )}
          <Title level={2} className="mb-2">
            {configLoading ? '加载中...' : siteConfig?.site_name || '创建账户'}
          </Title>
          <Text type="secondary">
            {configLoading ? '正在加载...' : siteConfig?.site_description || '注册游戏服务器控制面板账户'}
          </Text>
        </div>

        <Form
          name="register"
          onFinish={onFinish}
          size="large"
          autoComplete="off"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, message: '用户名至少3个字符' },
              { max: 20, message: '用户名最多20个字符' },
              {
                pattern: /^[a-zA-Z0-9_]+$/,
                message: '用户名只能包含字母、数字和下划线，不能使用中文'
              }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="用户名（仅限字母、数字、下划线）"
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="邮箱地址"
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6个字符' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="密码"
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="确认密码"
              className="rounded-lg"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="w-full h-12 rounded-lg font-medium"
            >
              注册
            </Button>
          </Form.Item>
        </Form>

        <Divider>或</Divider>

        <div className="text-center">
          <Text type="secondary">已有账户？</Text>
          <Link to="/login" className="ml-2 text-blue-500 hover:text-blue-600">
            立即登录
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default Register;



